/**
 * Frontend types for the scenario-level eval system.
 * Mirrors Backend/functions/src/eval/scenarioTypes.ts (+ compareScenarioRuns.ts).
 */

import type { IdealAssessment } from './index';

export interface ScenarioLabel {
    assessment: IdealAssessment;
    confidence?: 'high' | 'medium' | 'low';
    notes?: string;
    labeledBy?: string;
    labeledAt?: string;
}

export interface ScenarioInputQuestion {
    id: string;
    text: string;
    subject: string;
    topic: string;
    difficulty: string;
    questionType: string;
    correctAnswer?: string;
    imageUrl?: string;
}
export interface ScenarioInputProfile { class?: string; proficiency?: string; targetExam?: string }
export interface ScenarioInputSolution {
    imageUrl?: string;
    textAnswer?: string;
    selectedOption?: string;
    timeTakenSeconds?: number;
    inputMode?: string;
}

export interface ScenarioInput {
    id: string;
    datasetId: string;
    operation: string;
    source: 'captured' | 'authored';
    question: ScenarioInputQuestion;
    studentProfile: ScenarioInputProfile;
    studentSolution: ScenarioInputSolution;
    label?: ScenarioLabel;
    /** Production LLM's own assessment at capture — seeds the label form. */
    suggestedAssessment?: IdealAssessment;
    capturedFrom?: { submissionId?: string; modelInputId?: string };
    /** Bench curation metadata — hand-set, descriptive only, outside the hash. */
    variantQuality?: VariantQuality;
    difficultyTags?: string[];
    curationNote?: string;
    contentHash: string;
    createdAt: string;
    updatedAt?: string;
}

export type VariantQuality = 'correct' | 'partially_correct' | 'common_mistake';

export const READING_DIFFICULTY_TAGS = [
    'messy-handwriting', 'crossed-out', 'non-linear-layout', 'complex-notation',
    'student-diagram', 'faint-or-skewed', 'confusable-glyphs',
] as const;

export const ASSESSING_DIFFICULTY_TAGS = [
    'unconventional-method', 'propagated-error', 'multipart-carry-forward',
    'sign-vs-conceptual', 'skipped-steps', 'diverges-from-solution', 'restarted-attempt',
] as const;

export type ScenarioRunStatus = 'running' | 'completed' | 'completed_with_failures' | 'failed';

export interface DimensionStats { mean: number; median: number; stdDev: number; min: number; max: number }
export interface ScenarioRunStatistics {
    scored: number;
    unlabeled: number;
    dimensions: Record<string, DimensionStats>;
    aggregate: { mean: number; median: number; stdDev: number };
}

/** One pinned prompt inside a run's frozen config snapshot. */
export interface ResolvedPrompt {
    promptId: string;
    version: number;
    userPrompt: string;
    systemPrompt?: string;
    jsonSchema?: { name: string; schema: object };
}

/** Resolved per-provider params actually in play for a run. */
export interface ResolvedProviderParams {
    providerKey: string;
    modelName: string;
    temperature?: number;
    maxTokens?: number;
    reasoningEffort?: string;
    reasoningSummary?: string;
    thinkingBudgetTokens?: number;
    pricing?: { input: number; output: number };
}

/**
 * Deep, fully-resolved config snapshot frozen on each run (server-side
 * `ResolvedSnapshot`). Carries every parameter that defines the run's
 * behaviour — used by the per-run Parameters panel and the two-run config
 * diff. `operationConfig` / `providerSets` / `targeting` are rendered/diffed
 * generically, so they're kept loosely typed here.
 */
export interface ResolvedSnapshot {
    operation: string;
    pipelineConfigName: string;
    operationConfig: unknown;
    prompts: Record<string, ResolvedPrompt>;
    providerSets: { tiered: Record<string, unknown>; parallel: Record<string, unknown> };
    providerDefaults: Record<string, ResolvedProviderParams>;
    modelIds: string[];
    executionMode: string;
    targeting?: unknown;
    configFingerprint: string;
    codeVersion: string;
    snapshotAt: string;
}

export interface ScenarioEvalRun {
    id: string;
    datasetId: string;
    readableId: string;
    operation: string;
    pipelineConfigName: string;
    /** Frozen, fully-resolved config snapshot (prompts, providers, params). */
    snapshot?: ResolvedSnapshot;
    configFingerprint: string;
    codeVersion: string;
    rubricVersion?: string;
    status: ScenarioRunStatus;
    total: number;
    statistics?: ScenarioRunStatistics;
    isBaseline?: boolean;
    triggeredBy?: string;
    startedAt: string;
    completedAt?: string;
}

export interface ScenarioRunProgress {
    run: ScenarioEvalRun;
    progress: { total: number; completed: number; failed: number; remaining: number };
}

export interface DimensionResult { score: number; confidence?: 'high' | 'medium' | 'low'; details: Record<string, unknown> }
export interface EvalVector {
    aggregate: { weightedScore: number; weights: Record<string, number> };
    [dim: string]: DimensionResult | { weightedScore: number; weights: Record<string, number> };
}

export interface ScenarioEvalRunItemResult {
    id: string;
    datasetId: string;
    runId: string;
    scenarioInputId: string;
    scenarioInputHash: string;
    status: 'completed' | 'failed';
    outputId?: string;
    /** Every step's output — see the bench matrix. */
    outputs?: ScenarioEvalCellResult[];
    submissionId?: string;
    userId?: string;
    provider?: string;
    model?: string;
    error?: string;
    latencyMs?: number;
    costUsd?: number;
    completedAt: string;
    evalVector?: EvalVector;
    scoredRubricVersion?: string;
}

// ─── Comparison report ─────────────────────────────────────────────────

export interface ItemDelta {
    scenarioInputId: string;
    baselineScore?: number;
    candidateScore?: number;
    delta?: number;
    status: 'regressed' | 'improved' | 'unchanged' | 'only_baseline' | 'only_candidate';
    dimensionThatMoved?: string;
    correctnessFlip?: { baseline?: string; candidate?: string };
    inputChanged?: boolean;
}
export interface DimensionDelta { dimension: string; baselineMean: number; candidateMean: number; delta: number }
export interface AxisDiff {
    configChanged: boolean; config: { baseline: string; candidate: string };
    codeChanged: boolean; code: { baseline: string; candidate: string };
    rubricChanged: boolean; rubric: { baseline?: string; candidate?: string };
    inputsChanged: string[];
}
export interface ComparisonReport {
    baselineRunId: string;
    candidateRunId: string;
    datasetId: string;
    axisDiff: AxisDiff;
    summary: {
        baselineMean: number; candidateMean: number; meanDelta: number;
        pairedItemCount: number; meanItemDelta: number; stdDevItemDelta: number; significant: boolean;
        regressed: number; improved: number; unchanged: number; correctnessFlips: number;
    };
    dimensionDeltas: DimensionDelta[];
    items: ItemDelta[];
}

export interface CaptureResult { captured: number; skipped: number; scenarioInputIds: string[] }
export interface ScenarioInputsList { inputs: ScenarioInput[]; total: number; labeled: number }

// ─── LLM-as-judge ──────────────────────────────────────────────────────
export interface JudgeVerdict { scenarioInputId: string; dimension: string; score: number; equivalent: boolean; reasoning: string }
export interface JudgeRun {
    id: string;
    datasetId: string;
    evalRunId: string;
    status: 'judging' | 'completed' | 'failed';
    judgePromptId?: string;
    judgePromptVersion?: number;
    provider?: string;
    batch: { scenarioInputId: string; dimension: string; question: string; modelAnswer: string; acceptedExpected: string[] }[];
    verdicts?: JudgeVerdict[];
    modelInputId?: string;
    modelOutputId?: string;
    error?: string;
    createdAt: string;
    completedAt?: string;
}
export interface JudgeRunsList { judgeRuns: JudgeRun[]; total: number }

// ─── Assessment eval bench ─────────────────────────────────────────────
// Mirrors Backend/functions/src/eval/verdictTypes.ts + benchEndpoints.ts.
// See docs/FeatureDesigns/Evals/assessment-eval-bench.md.

/** One prompt execution — a single LLM call at one pipeline step. This, not the
 *  pipeline result, is what gets judged: `extract_visuals` and
 *  `analyze_visual_*` are different jobs with different right answers for which
 *  model to use. */
export interface ScenarioEvalCellResult {
    outputId: string;
    cellKey: string;
    pipelineName: string;
    stepIndex: number;
    stepType: string;
    promptId?: string;
    promptVersion?: number;
    provider?: string;
    model?: string;
    isPrimary: boolean;
    status: 'completed' | 'skipped';
    latencyMs?: number;
    costUsd?: number;
    /** What this cell concluded, on assessment/combined steps. Not a verdict —
     *  it is here so the matrix can show that two models scored the same
     *  solution 0 and 10. */
    score?: number;
}

export type VerdictValue = 'good' | 'acceptable' | 'bad';
export type StepRole = 'reading' | 'assessment' | 'scoring' | 'combined';

export interface EvalVerdict {
    id: string;
    datasetId: string;
    scenarioInputId: string;
    outputId: string;
    scenarioInputHash: string;
    cellKey: string;
    pipelineName: string;
    stepIndex: number;
    stepType: string;
    provider?: string;
    model?: string;
    evaluator: 'human' | 'judge';
    source: 'bench' | 'auto';
    verdict: VerdictValue;
    conditionalVerdict?: VerdictValue;
    upstreamFailure?: boolean;
    failureTags: string[];
    critique?: string;
    dimensionScores?: Record<string, { score: number; reasoning?: string }>;
    by?: string;
    at: string;
}

export interface EvalRowRanking {
    id: string;
    datasetId: string;
    scenarioInputId: string;
    scenarioInputHash: string;
    evaluator: 'human' | 'judge';
    ranking: string[];
    rankingCellKeys?: string[];
    note?: string;
    by?: string;
    at: string;
}

export interface MatrixCell extends ScenarioEvalCellResult {
    role: StepRole;
    /** Produced outside a bench run (found via the row's source ModelInput).
     *  Readable, but produced under drifting prompt versions — never averaged. */
    historical: boolean;
    humanVerdict?: EvalVerdict;
    judgeVerdict?: EvalVerdict;
    /** A verdict exists but was made against an older version of this row. */
    staleVerdict: boolean;
}

export interface MatrixChain {
    runId?: string;
    pipelineConfigName?: string;
    pipelineName: string;
    startedAt?: string;
    cells: MatrixCell[];
}

export interface MatrixRow {
    input: ScenarioInput;
    chains: MatrixChain[];
    ranking?: EvalRowRanking;
    /** Who produced the solution, and when — resolved from the source
     *  ModelInput. */
    userId?: string;
    userName?: string;
    submittedAt?: string;
}

export interface BenchMatrix {
    datasetId: string;
    rows: MatrixRow[];
    cellKeys: string[];
    counts: { rows: number; cells: number; judged: number; stale: number };
}

/** A bench declared in code (benchRegistry.ts), resolved to its dataset. */
export interface ResolvedBench {
    key: string;
    readableId: string;
    name: string;
    description: string;
    category: string;
    operation: string;
    datasetId: string;
}

export interface CandidateSubmission {
    modelInputId: string;
    submissionId?: string;
    userId?: string;
    createdAt?: string;
    hasImageAnswer: boolean;
    imageUrl?: string;
    textAnswer?: string;
    outputCount: number;
    verdict?: string;
    score?: number;
    stepCount?: number;
    alreadyAdded: boolean;
}

/** Failure vocabulary, split by the step it indicts. Tags aggregate; the
 *  free-text critique explains. */
export const READING_FAILURE_TAGS = [
    'misread-digit', 'misread-symbol', 'dropped-step', 'dropped-marginal-work',
    'invented-work', 'invented-line', 'garbled-layout', 'missed-diagram',
    'no-transcription',
] as const;

export const ASSESSMENT_FAILURE_TAGS = [
    'wrong-verdict', 'missed-error', 'false-error-flag', 'wrong-answer-extraction',
    'score-too-high', 'score-too-low', 'wrong-approach-call', 'no-carry-forward-credit',
] as const;

export const FEEDBACK_FAILURE_TAGS = [
    'feedback-incorrect', 'feedback-vague', 'wrong-level', 'gives-away-answer',
    'too-verbose', 'bad-tone', 'bad-latex',
] as const;

export const OPERATIONAL_FAILURE_TAGS = ['parse-failure', 'timeout', 'empty-output'] as const;

/** Which tags to offer for a step — a reading step can't have a "wrong verdict". */
export function tagsForRole(role: StepRole): readonly string[] {
    switch (role) {
        case 'reading': return [...READING_FAILURE_TAGS, ...OPERATIONAL_FAILURE_TAGS];
        case 'scoring': return [...OPERATIONAL_FAILURE_TAGS, 'score-too-high', 'score-too-low'];
        case 'combined': return [
            ...READING_FAILURE_TAGS, ...ASSESSMENT_FAILURE_TAGS,
            ...FEEDBACK_FAILURE_TAGS, ...OPERATIONAL_FAILURE_TAGS,
        ];
        default: return [
            ...ASSESSMENT_FAILURE_TAGS, ...FEEDBACK_FAILURE_TAGS, ...OPERATIONAL_FAILURE_TAGS,
        ];
    }
}

// ─── Scorecard + agreement ─────────────────────────────────────────────
// Mirrors Backend/functions/src/eval/benchScorecard.ts.

/** Where a verdict's cell came from: a run the bench launched, or the output
 *  production shipped. Never pooled — see assessment-eval-bench.md §10. */
export type VerdictSource = 'bench' | 'auto';

export interface CellStats {
    cellKey: string;
    /** Replayed on the bench, or the output that actually shipped. Part of the
     *  row's identity — the same cellKey appears once per source. */
    source: VerdictSource;
    role: StepRole;
    pipelineName: string;
    stepType: string;
    provider?: string;
    model?: string;
    promptVersions: number[];
    n: number;
    good: number;
    acceptable: number;
    bad: number;
    /** (good + acceptable/2) / n */
    quality: number;
    conditionalN: number;
    conditionalQuality?: number;
    /** Positive = this step was let down by the one before it. */
    upstreamPenalty?: number;
    failureTags: Record<string, number>;
    wins: number;
    ranked: number;
    winRate?: number;
    latencyMsP50?: number;
    latencyMsP95?: number;
    costUsdMean?: number;
}

export interface Scorecard {
    datasetId: string;
    evaluator: 'human' | 'judge';
    byRole: Record<string, CellStats[]>;
    totals: { cells: number; verdicts: number; rowsJudged: number };
    modelsSpanningPromptVersions: string[];
}

export interface AgreementStats {
    n: number;
    rawAgreement: number;
    kappa?: number;
    binaryAgreement: number;
    judgeHarsher: number;
    judgeLenient: number;
    confusion: Record<string, number>;
}

export interface AgreementReport {
    overall: AgreementStats;
    byRole: Record<string, AgreementStats>;
    disagreements: Array<{
        scenarioInputId: string;
        outputId: string;
        cellKey: string;
        role: StepRole;
        human: VerdictValue;
        judge: VerdictValue;
        humanCritique?: string;
        judgeCritique?: string;
    }>;
}

export interface JudgeBenchResult {
    cellsConsidered: number;
    cellsJudged: number;
    batches: number;
    failedBatches: number;
}
